import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import { MdOutlineCategory } from 'react-icons/md';
import { toast } from 'react-toastify';
import Card from '../../../components/Global/Card';
import DataTable from '../../../components/Global/DataTable';
import Modal from '../../../components/Global/Modal';
import GoBack from '../../../components/Utils/GoBack';
import { CustomService } from '../../../services/api/custom/CustomService';

const SegmentosPage = () => {
  const [segmentos, setSegmentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSegmento, setSelectedSegmento] = useState(null);
  const [nome, setNome] = useState('');
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsPage, setProductsPage] = useState(1);
  const [productsPages, setProductsPages] = useState(1);
  const [productsSegmento, setProductsSegmento] = useState(null);

  const fetchSegmentos = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await CustomService.getRequest('/interno/segmentos');
      setSegmentos(Array.isArray(resp) ? resp : []);
    } catch (error) {
      console.error('Erro ao buscar segmentos:', error);
      toast.error('Erro ao carregar segmentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSegmentos();
  }, [fetchSegmentos]);

  const openCreateModal = () => {
    setIsEditing(false);
    setSelectedSegmento(null);
    setNome('');
    setShowModal(true);
  };

  const openEditModal = (record) => {
    setIsEditing(true);
    setSelectedSegmento(record);
    setNome(record?.nome || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setSelectedSegmento(null);
    setNome('');
  };

  const closeProductsModal = () => {
    setShowProductsModal(false);
    setProducts([]);
    setProductsPage(1);
    setProductsPages(1);
    setProductsSegmento(null);
  };

  const fetchProdutosSegmento = useCallback(async (segmentoId, page = 1, replace = false) => {
    if (!segmentoId) return;
    setProductsLoading(true);
    try {
      const resp = await CustomService.getRequest(
        `/produtos/segmentos/${segmentoId}?page=${page}&perPage=40`
      );
      const list = Array.isArray(resp?.data) ? resp.data : [];
      const totalPages = Number.isFinite(Number(resp?.pages)) ? Number(resp.pages) : 1;
      setProductsPages(totalPages);
      setProductsPage(page);
      setProducts((prev) => (replace ? list : [...prev, ...list]));
    } catch (error) {
      console.error('Erro ao buscar produtos do segmento:', error);
      toast.error('Erro ao carregar produtos do segmento.');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  const openProductsModal = (record) => {
    closeModal();
    setProducts([]);
    setProductsPage(1);
    setProductsPages(1);
    setProductsSegmento(record);
    setShowProductsModal(true);
    fetchProdutosSegmento(record?.id, 1, true);
  };

  const loadMoreProducts = () => {
    if (!productsSegmento?.id) return;
    if (productsLoading || productsPage >= productsPages) return;
    fetchProdutosSegmento(productsSegmento.id, productsPage + 1, false);
  };

  const handleSave = async () => {
    const trimmed = nome.trim();
    if (!trimmed) {
      toast.warn('Informe o nome do segmento.');
      return;
    }

    try {
      if (isEditing && selectedSegmento?.id) {
        await CustomService.patchRequest(
          `/interno/segmentos/${selectedSegmento.id}`,
          { nome: trimmed }
        );
        toast.success('Segmento atualizado com sucesso!');
      } else {
        await CustomService.postRequest('/interno/segmentos', { nome: trimmed });
        toast.success('Segmento criado com sucesso!');
      }
      closeModal();
      fetchSegmentos();
    } catch (error) {
      console.error('Erro ao salvar segmento:', error);
      toast.error('Erro ao salvar segmento.');
    }
  };

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id' },
      { title: 'Nome', dataIndex: 'nome', key: 'nome' },
    ],
    []
  );

  const productsColumns = useMemo(
    () => [
      { title: 'Produto', dataIndex: 'nome', key: 'nome' },
      {
        title: 'Prioridade',
        dataIndex: 'prioridade',
        key: 'prioridade',
        render: (value) => (Number.isFinite(Number(value)) ? Number(value) : '-'),
      },
    ],
    []
  );

  const actions = {
    onEdit: (record) => openEditModal(record),
    onView: (record) => openProductsModal(record),
  };

  return (
    <div className="flex flex-col w-full gap-3">
      <GoBack path="/dashboard" />
      {showModal && (
        <Modal
          buttonName={isEditing ? 'Salvar' : 'Criar'}
          title={isEditing ? 'Editar segmento' : 'Novo segmento'}
          handleModal={(open) => (open ? setShowModal(true) : closeModal())}
          eventConfirmation={handleSave}
          size="md:w-1/3"
        >
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Nome do segmento</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:border-blue-500"
              placeholder="Ex.: Clínicas"
            />
          </div>
        </Modal>
      )}

      {showProductsModal && (
        <Modal
          buttonName="Fechar"
          title={
            productsSegmento?.nome
              ? `Produtos do segmento: ${productsSegmento.nome}`
              : 'Produtos do segmento'
          }
          handleModal={(open) =>
            open ? setShowProductsModal(true) : closeProductsModal()
          }
          eventConfirmation={closeProductsModal}
          size="md:w-2/3"
        >
          <div className="flex flex-col gap-3">
            <DataTable
              size="100%"
              data={products}
              columns={productsColumns}
              loading={productsLoading}
              actions={null}
            />
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                Página {productsPage} de {productsPages}
              </span>
              {productsPage < productsPages && (
                <Button
                  type="primary"
                  style={{ backgroundColor: '#054F72' }}
                  onClick={loadMoreProducts}
                  disabled={productsLoading}
                >
                  Carregar mais
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}

      <Card title="Segmentos" icon={<MdOutlineCategory className="w-[25px] h-[25px]" />}>
        <div className="p-4 flex flex-col gap-4 w-full">
          <div className="flex justify-end">
            <Button
              type="primary"
              style={{ backgroundColor: '#054F72' }}
              onClick={openCreateModal}
            >
              Novo Segmento
            </Button>
          </div>
          <DataTable size="100%" data={segmentos} columns={columns} loading={loading} actions={actions} />
        </div>
      </Card>
    </div>
  );
};

export default SegmentosPage;
